import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/authcontext';
import LoginPage from './pages/loginpage';
import DashboardPage from './pages/dashboardpage';
import AddressBookPage from './pages/addressbookpage';
import AnalyticsPage from './pages/analyticspage';
import NotFoundPage from './pages/notfoundpage';
import Sidebar from './components/sidebar/sidebar';
import Header from './components/Header';
import FeedbackForm from './components/FeedbackForm';
import Signup from './components/Signup';
import AccountSettings from './components/AccountSettings';
import PaymentStatusBanner from './components/PaymentStatusBanner';
import WhatsNewModal from './components/WhatsNewModal';
import AuthLayout from './layouts/AuthLayout';
import './App.css';
import InviteSignup from './pages/InviteSignup';
import PayPalSuccess from './pages/PayPalSuccess';

// Simple loading component
function LoadingScreen() {
  const [timeoutReached, setTimeoutReached] = React.useState(false);
  
  useEffect(() => {
    // Set a timeout for showing additional information if loading takes too long
    const timeoutId = setTimeout(() => {
      console.log('App.js: Loading timeout reached in LoadingScreen');
      setTimeoutReached(true);
    }, 3000); // Reduced to 3 seconds
    
    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <div className="loading-container">
      <div className="loading-message">
        <div className="loading-spinner"></div>
        <p>Loading zenbidup...</p>
        {timeoutReached && (
          <div className="loading-timeout-message">
            <p>This is taking longer than expected.</p>
            <p>Please make sure you're connected to the internet.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Error display component with auto-retry
function ErrorScreen({ error }) {
  console.log('App.js: Rendering ErrorScreen with error:', error);
  
  // Auto retry after a few seconds instead of waiting for user to click
  useEffect(() => {
    const retryTimeout = setTimeout(() => {
      console.log('App.js: Auto-retry initiated after error');
      // Try to use cached user data if available
      const cachedUser = localStorage.getItem('zenbidup_user');
      if (cachedUser) {
        console.log('App.js: Found cached user data, redirecting to dashboard');
        window.location.href = '/dashboard';
      } else {
        console.log('App.js: No cached user data, reloading page');
        window.location.reload();
      }
    }, 5000);
    
    return () => clearTimeout(retryTimeout);
  }, []);
  
  return (
    <div className="error-container">
      <div className="error-message">
        <h2>Something went wrong</h2>
        <p>{error}</p>
        <p>Attempting to reconnect automatically...</p>
        <button 
          onClick={() => {
            console.log('App.js: Retry button clicked, reloading page');
            window.location.reload();
          }}
          className="retry-button"
        >
          Retry Now
        </button>
      </div>
    </div>
  );
}

// Route guard for protected routes
function ProtectedRoute({ children }) {
  const { user, loading, error } = useAuth();
  const location = useLocation();
  
  // Log the current auth state and location
  useEffect(() => {
    console.log('App.js: ProtectedRoute - current path:', location.pathname);
    console.log('App.js: ProtectedRoute - auth state:', { 
      isAuthenticated: !!user, 
      loading, 
      hasError: !!error 
    });
  }, [user, loading, error, location]);
  
  // Check if cached user exists even if authentication is failing
  const tryCachedAuthentication = () => {
    const cachedUser = localStorage.getItem('zenbidup_user');
    return !!cachedUser;
  };
  
  if (loading) {
    console.log('App.js: ProtectedRoute - showing loading screen');
    return <LoadingScreen />;
  }
  
  if (error) {
    // If we have a cached user, ignore the error and show the protected content
    if (tryCachedAuthentication()) {
      console.log('App.js: ProtectedRoute - using cached authentication despite error');
      return children;
    }
    
    console.log('App.js: ProtectedRoute - showing error screen:', error);
    return <ErrorScreen error={error} />;
  }
  
  if (!user) {
    // Try to use cached authentication as fallback
    if (tryCachedAuthentication()) {
      console.log('App.js: ProtectedRoute - using cached authentication since user is null');
      return children;
    }
    
    console.log('App.js: ProtectedRoute - user not authenticated, redirecting to login');
    return <Navigate to="/login" replace />;
  }
  
  console.log('App.js: ProtectedRoute - rendering protected content');
  return children;
}

// Layout for the main dashboard with Sidebar and Header
const DashboardLayout = () => {
  const { user } = useAuth();

  return (
    <div className="app">
      <Sidebar />
      <main className="main-content">
        <PaymentStatusBanner />
        <Header />
        <Outlet />
      </main>
      <WhatsNewModal user={user} />
    </div>
  );
};

// Main app component
function App() {
  useEffect(() => {
    console.log('App.js: App component mounted');
    return () => {
      console.log('App.js: App component unmounted');
    };
  }, []);

  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Auth routes with a dedicated centered layout */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/invite" element={<InviteSignup />} />
          </Route>
          
          {/* PayPal Success - Public route */}
          <Route path="/paypal-success" element={<PayPalSuccess />} />
          
          {/* Protected dashboard routes with sidebar/header layout */}
          <Route element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="address-book/:type" element={<AddressBookPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="feedback" element={<FeedbackForm />} />
            <Route path="account-settings" element={<AccountSettings />} />
          </Route>

          {/* Fallback/Not Found Route */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
